import { useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell } from '@fortawesome/free-regular-svg-icons';
import notificationApi from '@/core/notifications/api/notification-api';
import { useNotificationServiceContext } from '@/core/notifications/contexts/notification-service-context';
import { MenuList, CheckboxRow } from '@/shared/components/export';

export default function SubscriptionChannelDropdown({
  subscriptionId,
}: {
  subscriptionId: string;
}) {
  const { subscriptionById, channels, refreshSubscriptions } = useNotificationServiceContext();
  const subscription = subscriptionById(subscriptionId);

  const [selected, setSelected] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  const handleOpen = () => {
    if (subscription) setSelected([...subscription.defaultChannels]);
  };

  const toggle = (key: string) =>
    setSelected(prev => (prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]));

  const handleSave = async (close: () => void) => {
    if (!subscription) return;
    setSaving(true);
    try {
      const channelsToSave = selected.includes('in-app') ? selected : ['in-app', ...selected];
      await notificationApi.updateSubscription(subscription.id, {
        defaultChannels: channelsToSave,
      });
      refreshSubscriptions();
      close();
    } finally {
      setSaving(false);
    }
  };

  return (
    <MenuList
      trigger={<FontAwesomeIcon icon={faBell} />}
      toggleTitle={subscription ? `Channels — ${subscription.name}` : 'Alarm alerts'}
      toggleDisabled={!subscription}
      align="right"
      minWidth={220}
      onOpen={handleOpen}
      elevated
      footer={close =>
        subscription && (
          <>
            <div
              className={`text-truncate mb-1 mutedText`}
              style={{
                fontSize: 11,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                fontWeight: 700,
              }}
            >
              {subscription.name}
            </div>
            <div className={`mb-2 mutedText`} style={{ fontSize: 12 }}>
              Delivery channels
            </div>

            {channels.map(ch => {
              const isLocked = ch.key === 'in-app';
              return (
                <CheckboxRow
                  key={ch.key}
                  className="mb-2"
                  checked={isLocked || selected.includes(ch.key)}
                  disabled={isLocked}
                  onChange={() => !isLocked && toggle(ch.key)}
                  label={ch.displayName}
                />
              );
            })}

            <div className="d-flex justify-content-end gap-2 mt-3 pt-2 border-top">
              <button className="btn btn-link btn-sm p-0" style={{ fontSize: 12 }} onClick={close}>
                Cancel
              </button>
              <button
                className="btn btn-primary btn-sm"
                style={{ fontSize: 12 }}
                onClick={() => handleSave(close)}
                disabled={saving}
              >
                {saving ? <Spinner size="sm" animation="border" /> : 'Save'}
              </button>
            </div>
          </>
        )
      }
    />
  );
}
